"use client";

import * as React from "react";
import { useLingui } from "@lingui/react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

interface TimePickerProps {
  id?: string;
  value?: string;
  onChange?: (time: string) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Linked to both segments so the error node is announced on either one. */
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  /** Required state reported by the surrounding form; set on both segments. */
  "aria-required"?: boolean | "true" | "false";
  /**
   * When `onChange` fires.
   *
   * - `"blur"` (default) — only on segment blur and arrow keys. Every form
   *   consumer relies on this: they see a value only once a segment is
   *   complete, so validation does not fire against half-typed input.
   * - `"live"` — additionally on every keystroke. Inline table cells need this:
   *   they commit from a ref they fill from `onChange`, so a picker that stays
   *   silent while typing leaves the commit path holding the pre-edit value and
   *   the edit is lost. Such consumers must keep `onChange` out of state that
   *   re-renders this component (see `EditableCell`'s time case).
   */
  commitMode?: "blur" | "live";
}

/**
 * Time picker with separate hour and minute inputs.
 * Based on OpenStatus time picker design but simplified for our use case.
 * Properly respects shadcn/ui theming.
 */
export function TimePicker({
  id,
  value = "",
  onChange,
  onBlur,
  name: _name,
  disabled = false,
  className,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  commitMode = "blur",
}: TimePickerProps) {
  const { i18n } = useLingui();
  const [hour, setHour] = React.useState<string>("");
  const [minute, setMinute] = React.useState<string>("");

  const hourRef = React.useRef<HTMLInputElement>(null);
  const minuteRef = React.useRef<HTMLInputElement>(null);

  // Last value this component pushed out through onChange in "live" mode.
  //
  // A consumer that echoes a live value straight back as `value` would feed the
  // sync effect below and overwrite the segment the user is mid-way through
  // typing ("9" padded back to "09" under the caret), so the effect skips
  // values it recognises as its own.
  const lastEmittedRef = React.useRef<string | null>(null);

  // Note: We intentionally avoid manual cursor management to prevent caret jumps

  // Parse the initial value
  React.useEffect(() => {
    if (lastEmittedRef.current !== null && value === lastEmittedRef.current) {
      return;
    }
    lastEmittedRef.current = null;
    if (value) {
      const [h, m] = value.split(":");
      setHour(h || "");
      setMinute(m || "");
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  // Report a composed value, skipping no-op emissions.
  //
  // The baseline is what we LAST reported, not the `value` prop. Once an
  // intermediate value has gone out (typing "08:30", arrowing away and back),
  // the consumer's commit path holds that intermediate; comparing against
  // `value` would then swallow the emission that returns to the original and
  // leave the stale intermediate to be saved.
  const emitComposed = (composed: string) => {
    const lastReported = lastEmittedRef.current ?? value;
    if (composed === lastReported) return;
    lastEmittedRef.current = composed;
    onChange?.(composed);
  };

  // Emit the composed value while typing (live mode only). An empty segment
  // means "no time" rather than a partial one — consumers already map "" to a
  // cleared field — so we only compose once both segments carry digits.
  const emitLive = (nextHour: string, nextMinute: string) => {
    if (commitMode !== "live") return;
    const composed =
      nextHour && nextMinute
        ? `${nextHour.padStart(2, "0")}:${nextMinute.padStart(2, "0")}`
        : "";
    emitComposed(composed);
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const val = input.value.replace(/\D/g, "");

    // maxLength={2} on input already prevents >2 chars, so we don't need val.length check
    // Removing it prevents race conditions when typing quickly
    if (val === "" || (parseInt(val) >= 0 && parseInt(val) <= 23)) {
      setHour(val);
      emitLive(val, minute);
      // Do not auto-advance on 2 digits; this caused lost keystrokes in some environments
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const val = input.value.replace(/\D/g, "");

    // maxLength={2} on input already prevents >2 chars, so we don't need val.length check
    // Removing it prevents race conditions when typing quickly
    if (val === "" || (parseInt(val) >= 0 && parseInt(val) <= 59)) {
      setMinute(val);
      emitLive(hour, val);
    }
  };

  const handleHourBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw) {
      const bounded = Math.max(0, Math.min(23, parseInt(raw, 10))).toString();
      const paddedHour = bounded.padStart(2, "0");
      setHour(paddedHour);
      if (minute) {
        const formattedMinute = minute.padStart(2, "0");
        const newValue = `${paddedHour}:${formattedMinute}`;
        emitComposed(newValue);
      }
    } else {
      // An emptied segment means "no time", not a half-typed one. Without this
      // report a time could be typed but never removed: the blur handlers only
      // ever emitted a composed value, so clearing a segment left the consumer
      // holding the old time (#1583, clearing a weekday drops its entry).
      //
      // BOTH segments are cleared, not just this one. The consumer echoes the
      // empty value straight back as `value`, which the sync effect then skips
      // as our own emission, so the companion segment would keep its digits
      // next to a value of "" -- and typing this segment again would recompose
      // the very time the user just cleared.
      setHour("");
      setMinute("");
      emitComposed("");
    }
    // Only fire form-level onBlur when focus leaves the entire TimePicker
    if (!minuteRef.current?.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  const handleMinuteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw) {
      const bounded = Math.max(0, Math.min(59, parseInt(raw, 10))).toString();
      const paddedMinute = bounded.padStart(2, "0");
      setMinute(paddedMinute);
      if (hour) {
        const formattedHour = hour.padStart(2, "0");
        const newValue = `${formattedHour}:${paddedMinute}`;
        emitComposed(newValue);
      }
    } else {
      // See handleHourBlur: an emptied segment clears the whole value, both
      // segments included.
      setHour("");
      setMinute("");
      emitComposed("");
    }
    // Only fire form-level onBlur when focus leaves the entire TimePicker
    if (!hourRef.current?.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const val = parseInt(hour || "0");
      const newHour = ((val + 1) % 24).toString().padStart(2, "0");
      setHour(newHour);
      if (minute) {
        const formattedMinute = minute.padStart(2, "0");
        const newValue = `${newHour}:${formattedMinute}`;
        emitComposed(newValue);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const val = parseInt(hour || "0");
      const newHour = ((val - 1 + 24) % 24).toString().padStart(2, "0");
      setHour(newHour);
      if (minute) {
        const formattedMinute = minute.padStart(2, "0");
        const newValue = `${newHour}:${formattedMinute}`;
        emitComposed(newValue);
      }
    } else if (e.key === "ArrowRight" || e.key === ":") {
      e.preventDefault();
      minuteRef.current?.focus();
    }
  };

  const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const val = parseInt(minute || "0");
      const newMinute = ((val + 1) % 60).toString().padStart(2, "0");
      setMinute(newMinute);
      if (hour) {
        const formattedHour = hour.padStart(2, "0");
        const newValue = `${formattedHour}:${newMinute}`;
        emitComposed(newValue);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const val = parseInt(minute || "0");
      const newMinute = ((val - 1 + 60) % 60).toString().padStart(2, "0");
      setMinute(newMinute);
      if (hour) {
        const formattedHour = hour.padStart(2, "0");
        const newValue = `${formattedHour}:${newMinute}`;
        emitComposed(newValue);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      hourRef.current?.focus();
    }
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <div className="relative flex items-center">
        <Input
          ref={hourRef}
          type="text"
          inputMode="numeric"
          draggable={false}
          value={hour}
          onChange={handleHourChange}
          onBlur={handleHourBlur}
          onKeyDown={handleHourKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onFocus={(e) => e.target.select()}
          placeholder="HH"
          maxLength={2}
          disabled={disabled}
          id={id}
          className="w-14 text-center px-2"
          aria-label={i18n._({ id: "common.timePicker.hours", message: "Hours" })}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
        />
        <span className="mx-1 text-muted-foreground">:</span>
        <Input
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          draggable={false}
          value={minute}
          onChange={handleMinuteChange}
          onBlur={handleMinuteBlur}
          onKeyDown={handleMinuteKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onFocus={(e) => e.target.select()}
          placeholder="MM"
          maxLength={2}
          disabled={disabled}
          className="w-14 text-center px-2"
          aria-label={i18n._({ id: "common.timePicker.minutes", message: "Minutes" })}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
        />
      </div>
    </div>
  );
}
